/**
 * useSessionActions — All session/game callbacks for App.tsx.
 */
import { useRef } from 'react';
import { useUIStore, useUserStore } from '../stores';
import { ApiClient } from '../services/apiClient';
import { setSentryUser, enableSentryTrackingIntegrations } from '../index';
import { supabase } from '../services/supabaseClient';
import logger from '../services/logger';
import { setAnalyticsConsent } from '../services/analytics-ga4';
import i18n from '../i18n';
import { Difficulty, type Question } from '../../shared/types';
import { buildPlacementTest, type PlacementDifficulty } from '../services/placementTest';
import { localDateString } from '../services/streak';
import { CHAPTERS } from '../constants';
import { trackEvent } from '../services/analytics';
import { canAccessFullChapter } from '../../shared/freeAccess';

// Fallback URL for handleUpgrade if /api/pricing is unavailable
const COPECART_CHECKOUT_URL_FALLBACK = 'https://copecart.com/products/24d84e3a/checkout';

export function useSessionActions() {
  const user = useUserStore((s) => s.user);
  const sessionData = useUserStore((s) => s.sessionData);
  const uiActions = useRef(useUIStore.getState());
  const userActions = useRef(useUserStore.getState());
  const t = i18n.t.bind(i18n);
  const showToast = (msg: string) => uiActions.current.showToast(msg);

  const handleLogin = () => {
    // NOTE: This is only called for email/password login from AuthView.
    // OAuth login is handled by the onAuthStateChange listener in useAppLifecycle.
    const u = ApiClient.getUser();
    userActions.current.setUser(u);
    if (u.isDarkMode) document.documentElement.classList.add('dark');
    // AUTH-FIX: For email/password login, verify onboarding status from DB
    // before showing onboarding. The localStorage value may be stale.
    if (!u.hasSeenOnboarding) {
      ApiClient.refreshUserFromSupabase().then((freshUser) => {
        if (freshUser) {
          userActions.current.setUser(freshUser);
          if (!freshUser.hasSeenOnboarding) {
            uiActions.current.setShowOnboarding(true);
          }
        } else {
          // Offline fallback — trust localStorage
          uiActions.current.setShowOnboarding(true);
        }
      });
    }
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab') === 'profile') {
      uiActions.current.setActiveTab('profile');
      window.history.replaceState(null, '', window.location.pathname);
    }
    if (params.get('page') === 'feedback') {
      uiActions.current.setShowBetaFeedback(true);
      window.history.replaceState(null, '', window.location.pathname);
    }
    if (params.get('page') === 'support') {
      uiActions.current.setActiveTab('profile');
      window.history.replaceState(null, '', window.location.pathname);
    }
  };

  const handleLogout = async () => {
    await ApiClient.logout();
    userActions.current.setUser(null);
    setSentryUser(null);
    userActions.current.setSessionData(null);
    uiActions.current.setActiveTab('learn');
  };

  const startChapter = (chapterId: number, difficulty: Difficulty | null = null) => {
    const currentUser = ApiClient.getUser();
    const isPreview = !canAccessFullChapter(currentUser, chapterId);
    const questions = ApiClient.getAccessibleQuestionsForChapter(chapterId, difficulty);
    if (questions.length === 0) {
      showToast(t('Keine Fragen verfügbar.'));
      return;
    }
    if (isPreview) {
      trackEvent('preview_chapter_started', {
        chapterId,
        freeCount: questions.length,
      });
    }
    userActions.current.setSessionData({
      questions,
      mode: 'learn',
      chapterId,
      isPreview,
    });
  };

  const startMistakes = () => {
    const q = ApiClient.getFailedQuestions();
    if (q.length === 0) {
      showToast(t('Keine Fehler vorhanden! Super!'));
      return;
    }
    userActions.current.setSessionData({ questions: q, mode: 'mistakes' });
  };

  const startSrs = () => {
    const q = ApiClient.getSrsQuestions();
    if (q.length === 0) {
      showToast(t('Keine Fragen fällig! Super!'));
      return;
    }
    userActions.current.setSessionData({ questions: q, mode: 'srs' });
  };

  const startSmartSession = () => {
    import('../services/smartSession').then(({ buildSmartSession }) => {
      const session = buildSmartSession(user);
      if (session.questions.length === 0) {
        showToast(t('smart_session_empty'));
        return;
      }
      userActions.current.setSessionData({ questions: session.questions, mode: 'smart' });
    });
  };

  const startExamWithConfidenceCheck = () => {
    if (!user?.isPremium && user?.simulationAttempts && user.simulationAttempts >= 3) {
      uiActions.current.openPaywall();
      return;
    }
    uiActions.current.setShowConfidenceCheck(true);
  };

  const startExam = () => {
    uiActions.current.setShowConfidenceCheck(false);
    // CONV-01: Track exam start funnel event
    trackEvent('exam_started', { questionCount: 30 });
    // F-08: count the attempt at START, not at completion — otherwise a
    // free user could start and abandon simulations indefinitely and the
    // 3-attempt gate meant nothing. Safe now that an interrupted session
    // can be resumed (F-09), so a crash no longer burns an attempt.
    const u = ApiClient.updateUser({
      simulationAttempts: (user?.simulationAttempts || 0) + 1,
    });
    userActions.current.setUser(u);
    userActions.current.setSessionData({
      questions: ApiClient.getRandomQuestions(30),
      mode: 'exam',
    });
  };

  const startHardTraining = () => {
    const hard = ApiClient.getAllQuestions()
      .filter((q) => q.difficulty === Difficulty.HARD)
      .sort(() => Math.random() - 0.5)
      .slice(0, 20);
    if (hard.length > 0) userActions.current.setSessionData({ questions: hard, mode: 'learn' });
  };

  const startPlacementTest = () => {
    const level: PlacementDifficulty =
      user?.experienceLevel === 'erfahren' ? 'erfahren' : 'fortgeschritten';
    const test = buildPlacementTest(level);
    if (test.questions.length === 0) {
      showToast(t('placement_no_questions'));
      return;
    }
    userActions.current.setSessionData({ questions: test.questions, mode: 'placement' });
  };

  const handleSessionComplete = (results: {
    correctCount: number;
    errorPoints: number;
    passed: boolean;
    chapterErrors?: Record<number, number>;
  }) => {
    if (sessionData?.mode === 'exam') {
      // simulationAttempts is NOT incremented here — startExam() already
      // counted this attempt (F-08).
      const u = ApiClient.updateUser({
        xp: (user?.xp || 0) + (results.passed ? 100 : 20),
        simulationHistory: [
          ...(user?.simulationHistory || []),
          {
            date: new Date().toISOString(),
            passed: results.passed,
            correctCount: results.correctCount,
            errorPoints: results.errorPoints,
            timeUsed: (results as any).timeUsed || 0,
            chapterErrors: results.chapterErrors || {},
          },
        ],
      });
      userActions.current.setUser(u);
      userActions.current.setExamResult({ ...results, chapterErrors: results.chapterErrors || {} });

      // CONV-01: Track exam completion and pass/fail
      trackEvent('exam_complete', {
        passed: results.passed,
        correctCount: results.correctCount,
        errorPoints: results.errorPoints,
      });
      if (results.passed) {
        trackEvent('exam_passed', {
          correctCount: results.correctCount,
          attempt: user?.simulationAttempts || 1,
        });
      }
    } else if (sessionData?.mode === 'placement') {
      // Build per-chapter results from chapterErrors. F-14: only chapters
      // that were actually tested may count — an empty chapter produces no
      // question, and "no errors" must not be scored as mastered (+XP,
      // +100 % progress).
      const chapterErrors = results.chapterErrors || {};
      const testedChapterIds = new Set(
        (sessionData?.questions || []).map((q: Question) => q.chapterId)
      );
      const chapterResults = CHAPTERS.filter((c) => testedChapterIds.has(c.id)).map((c) => ({
        chapterId: c.id,
        correct: (chapterErrors[c.id] || 0) === 0,
      }));
      const totalCorrect = chapterResults.filter((r) => r.correct).length;

      // Update chapterProgress based on placement results
      const currentProgress = { ...(user?.chapterProgress || {}) };
      chapterResults.forEach((r) => {
        currentProgress[r.chapterId] = r.correct ? 100 : 0;
      });

      const u = ApiClient.updateUser({
        xp: (user?.xp || 0) + totalCorrect * 10,
        chapterProgress: currentProgress,
      });
      userActions.current.setUser(u);
      userActions.current.setPlacementResult({
        chapterResults,
        totalCorrect,
        totalQuestions: chapterResults.length,
      });
    } else {
      // Non-exam, non-placement: show brief toast feedback
      const percent = sessionData?.questions?.length
        ? Math.round((results.correctCount / sessionData.questions.length) * 100)
        : 0;
      showToast(
        `${results.correctCount}/${sessionData?.questions?.length || 0} ${t('richtig')} (${percent}%) ✅`
      );
    }
    // F-13: LOCAL day boundary — with the UTC date, learning after
    // midnight still counted towards the previous day and the daily goal
    // only reset at 01:00/02:00 local time.
    const today = localDateString();
    const currentProgress =
      user?.dailyProgress?.date === today ? user?.dailyProgress?.count || 0 : 0;
    const updatedUser = ApiClient.updateUser({
      dailyProgress: {
        date: today,
        count: currentProgress + (sessionData?.questions?.length || 0),
      },
    });
    userActions.current.setUser(updatedUser);
    const completedSession = sessionData;
    userActions.current.setSessionData(null);

    // Free-tier preview paywall: when a free user finishes the 5-question slice
    // of a locked chapter, surface the contextual paywall right after the
    // results toast. Premium users and chapter 1 never reach this branch.
    if (completedSession?.isPreview && completedSession.chapterId !== undefined) {
      const chId = completedSession.chapterId;
      const totalCount = ApiClient.getQuestionsForChapter(chId).length;
      const freeCount = completedSession.questions.length;
      trackEvent('preview_limit_reached', { chapterId: chId, freeCount, totalCount });
      uiActions.current.setPremiumTrigger('preview', { chapterId: chId, freeCount, totalCount });
    }

    // CONV-02: Track session completion activity
    trackEvent('session_complete', {
      mode: sessionData?.mode || 'unknown',
      questionCount: sessionData?.questions?.length || 0,
      correctCount: results.correctCount,
    });

    // CONV-01: Track first_question milestone (one-time)
    if (user && (user.completedQuestions?.length || 0) <= (sessionData?.questions?.length || 0)) {
      // User just completed their very first session — they had 0 before
      const hadQuestionsBefore = (user.completedQuestions?.length || 0) > 0;
      if (!hadQuestionsBefore) {
        trackEvent('first_question', { mode: sessionData?.mode || 'unknown' });
      }
    }
  };

  const handleUpgrade = async () => {
    // Fetch the highlighted plan's checkout_url from DB (CONV-03)
    let checkoutUrl = COPECART_CHECKOUT_URL_FALLBACK;
    try {
      const resp = await fetch('/api/pricing');
      if (resp.ok) {
        const data = await resp.json();
        const plans: Array<{ checkout_url: string; is_highlighted: boolean }> = data.plans || [];
        const highlighted = plans.find((p) => p.is_highlighted) || plans[0];
        if (highlighted?.checkout_url) checkoutUrl = highlighted.checkout_url;
      }
    } catch {
      /* use fallback */
    }

    window.open(checkoutUrl, '_blank');
    // SECURITY: Poll the server-side premium-status endpoint (source of truth)
    // instead of trusting client-side data. Premium can only be set via webhook.
    const checkPremium = setInterval(async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session?.access_token) return;
        const res = await fetch('/api/user/premium-status', {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!res.ok) return;
        const status = await res.json();
        if (status.premium) {
          clearInterval(checkPremium);
          // Refresh full user data from Supabase (server-controlled fields)
          const refreshed = await ApiClient.refreshUserFromSupabase();
          if (refreshed) userActions.current.setUser(refreshed);
          uiActions.current.closePaywall();
          showToast(t('Premium aktiviert! Vielen Dank. 🎉'));
        }
      } catch (err) {
        logger.error('Fahryo', 'Premium poll failed:', err);
      }
    }, 5000);
    setTimeout(() => clearInterval(checkPremium), 300000);
  };

  const handleConsent = async (full: boolean) => {
    userActions.current.setUser(
      ApiClient.updateUser({ consentGiven: true, shareDataWithAI: full })
    );

    // GA4 Consent Mode v2 — accept grants analytics_storage, decline keeps it denied.
    // Ad-storage stays denied in both cases (Fahryo runs no ads).
    setAnalyticsConsent(full);

    // §25 TDDDG: enable consent-gated Sentry Session Replay + tracing only when
    // the user granted analytics/tracking consent in this session.
    if (full) enableSentryTrackingIntegrations();

    // DSGVO-02: Persist consent to server (audit trail with version).
    // terms + privacy are required to use the service (always accepted on the
    // banner); ai_data_sharing reflects the user's actual analytics/AI choice so
    // the consent_log records what was truly granted — not a blanket "true".
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (token) {
        const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:3000' : '';
        const consents: { type: string; accepted: boolean }[] = [
          { type: 'terms', accepted: true },
          { type: 'privacy', accepted: true },
          { type: 'ai_data_sharing', accepted: full },
        ];
        await Promise.allSettled(
          consents.map((c) =>
            fetch(`${apiBase}/api/user/consent`, {
              method: 'POST',
              headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
              body: JSON.stringify({ consentType: c.type, accepted: c.accepted }),
            })
          )
        );
      }
    } catch {
      /* Non-blocking */
    }
  };

  return {
    handleLogin,
    handleLogout,
    startChapter,
    startMistakes,
    startSrs,
    startSmartSession,
    startExamWithConfidenceCheck,
    startExam,
    startHardTraining,
    startPlacementTest,
    handleSessionComplete,
    handleUpgrade,
    handleConsent,
  };
}
